<template>
  <section class="section">
    <div class="container">
      <div class="page-head">
        <div>
          <h1 class="page-title">Estadística de asistencias</h1>
          <p class="page-sub">{{ subtitulo }}</p>
        </div>
      </div>

      <div class="stat-cards">
        <div class="stat-card tint-violet">
          <div class="stat-label">Miembros</div>
          <div class="stat-value">{{ miembros.length }}</div>
          <div class="stat-caption">En el contexto actual</div>
        </div>
        <div class="stat-card tint-blue">
          <div class="stat-label">Asistencias</div>
          <div class="stat-value">{{ totales.presencias }}</div>
          <div class="stat-caption">En el periodo filtrado</div>
        </div>
        <div class="stat-card tint-amber">
          <div class="stat-label">Asistencias posibles</div>
          <div class="stat-value">{{ totales.posibles }}</div>
          <div class="stat-caption">Según el horario del grupo</div>
        </div>
        <div class="stat-card tint-green">
          <div class="stat-label">% general</div>
          <div class="stat-value">{{ totales.porcentaje }}%</div>
          <div class="stat-caption">Promedio del periodo</div>
        </div>
      </div>

      <div class="toolbar-panel">
        <p class="toolbar-label">Filtros</p>
        <b-field grouped>
          <b-input placeholder="Buscar miembro por nombre" v-model="filtro" icon="magnify" expanded></b-input>
          <b-select v-model="anio" @input="cargar">
            <option :value="null">Todos los años</option>
            <option v-for="a in anios" :key="a" :value="a">{{ a }}</option>
          </b-select>
          <b-select v-model="mes" @input="cargar">
            <option :value="null">Todos los meses</option>
            <option v-for="(n, i) in mesesNombres" :key="i + 1" :value="i + 1">{{ n }}</option>
          </b-select>
          <b-select v-model="hora" @input="cargar">
            <option :value="null">Todos los horarios</option>
            <option v-for="h in horas" :key="h" :value="h">{{ etiquetaHora(h) }}</option>
          </b-select>
          <b-select v-model="orden">
            <option value="nombre">Ordenar: nombre</option>
            <option value="alto">Mayor % primero</option>
            <option value="bajo">Menor % primero</option>
          </b-select>
        </b-field>
      </div>

      <div v-if="cargando" class="est-loading">Cargando estadísticas…</div>

      <div v-else-if="!miembrosFiltrados.length" class="empty-card">
        <b-icon icon="chart-line" size="is-large"></b-icon>
        <p>No hay miembros{{ filtro ? ' que coincidan con la búsqueda' : '' }}.</p>
      </div>

      <div v-else class="est-panel">
        <table class="est-table">
          <thead>
            <tr>
              <th style="width:34px"></th>
              <th>Miembro</th>
              <th v-if="vistaGlobalSuper">Grupo</th>
              <th>Estado</th>
              <th class="c">Asistencias</th>
              <th class="c">Posibles</th>
              <th class="c" style="width:150px">%</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="m in miembrosFiltrados">
              <tr :key="'r' + m.id" class="est-row" @click="toggle(m.id)">
                <td><b-icon :icon="abierto[m.id] ? 'chevron-down' : 'chevron-right'" size="is-small"></b-icon></td>
                <td class="est-nombre">{{ nombreCompleto(m) }}</td>
                <td v-if="vistaGlobalSuper"><span class="tag is-light">{{ m.grupo_nombre || '—' }}</span></td>
                <td><span class="estado" :class="estadoClass(m.estado_asistencia)">{{ m.estado_asistencia || '—' }}</span></td>
                <td class="c">{{ m.presencias }}</td>
                <td class="c">{{ m.posibles }}</td>
                <td class="c">
                  <div class="mini-wrap">
                    <div class="mini-bar"><div class="mini-fill" :class="pctClass(m.porcentaje)" :style="{ width: m.porcentaje + '%' }"></div></div>
                    <span class="pct" :class="pctClass(m.porcentaje)">{{ m.porcentaje }}%</span>
                  </div>
                </td>
              </tr>
              <tr v-if="abierto[m.id]" :key="'d' + m.id" class="est-detail">
                <td :colspan="vistaGlobalSuper ? 7 : 6">
                  <div class="hist-cols">
                    <div class="hist-col">
                      <p class="hist-sub">Mes a mes</p>
                      <table class="hist-table">
                        <thead><tr><th>Mes</th><th>Asistió</th><th>Posibles</th><th>%</th></tr></thead>
                        <tbody>
                          <tr v-for="mm in mesesDesc(m)" :key="mm.anio + '-' + mm.mes">
                            <td>{{ mesesNombres[mm.mes - 1] }} {{ mm.anio }}</td>
                            <td>{{ mm.presencias }}</td>
                            <td>{{ mm.posibles }}</td>
                            <td><span class="pct" :class="pctClass(mm.porcentaje)">{{ mm.porcentaje }}%</span></td>
                          </tr>
                          <tr v-if="!m.meses.length"><td colspan="4" class="hist-vacio">Sin meses en el periodo.</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="hist-col">
                      <p class="hist-sub">Por horario</p>
                      <table class="hist-table">
                        <thead><tr><th>Horario</th><th>Asistió</th><th>Posibles</th><th>%</th></tr></thead>
                        <tbody>
                          <tr v-for="h in m.por_hora" :key="h.hora">
                            <td>{{ etiquetaHora(h.hora) }}</td>
                            <td>{{ h.presencias }}</td>
                            <td>{{ h.posibles }}</td>
                            <td><span class="pct" :class="pctClass(h.porcentaje)">{{ h.posibles ? h.porcentaje + '%' : '—' }}</span></td>
                          </tr>
                          <tr v-if="!m.por_hora.length"><td colspan="4" class="hist-vacio">Sin registros.</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import AyudanteSesion from '../../Servicios/AyudanteSesion'

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
const HORAS = {
  '05:00': 'Oración 5:00 AM',
  '07:00': 'Dominical 7:00 AM',
  '09:00': 'Oración 9:00 AM',
  '18:00': 'Servicio especial 6:00 PM',
  '19:00': 'Oración 7:00 PM',
  '(otro)': 'Otro horario'
}

export default {
  name: 'EstadisticaAsistencias',
  data(){
    return {
      cargando: true,
      miembros: [],
      totales: { presencias: 0, posibles: 0, porcentaje: 0 },
      anios: [],
      horas: [],
      anio: new Date().getFullYear(),
      mes: new Date().getMonth() + 1,
      hora: null,
      filtro: '',
      orden: 'nombre',
      abierto: {},
      mesesNombres: MESES
    }
  },
  computed: {
    esSuper(){ return AyudanteSesion.esSuper() },
    vistaGlobalSuper(){ return this.esSuper && !AyudanteSesion.actuandoComoGrupo() && !localStorage.getItem('grupoSeleccionado') },
    subtitulo(){
      const g = localStorage.getItem('grupoSeleccionadoNombre') || localStorage.getItem('grupoNombre')
      return g ? ('Asistencias del grupo ' + g) : 'Asistencias de todos los grupos'
    },
    miembrosFiltrados(){
      let lista = this.miembros
      const f = (this.filtro || '').trim().toLowerCase()
      if(f) lista = lista.filter(m => this.nombreCompleto(m).toLowerCase().indexOf(f) !== -1)
      lista = [...lista]
      if(this.orden === 'alto') lista.sort((a, b) => b.porcentaje - a.porcentaje)
      else if(this.orden === 'bajo') lista.sort((a, b) => a.porcentaje - b.porcentaje)
      else lista.sort((a, b) => this.nombreCompleto(a).localeCompare(this.nombreCompleto(b), 'es'))
      return lista
    }
  },
  mounted(){ this.cargar() },
  methods: {
    nombreCompleto(m){ return ((m.nombre || '') + ' ' + (m.apellido || '')).trim() || 'Sin nombre' },
    etiquetaHora(h){ return HORAS[h] || h },
    pctClass(p){
      if(p >= 60) return 'pct-alta'
      if(p >= 30) return 'pct-media'
      return 'pct-baja'
    },
    estadoClass(e){
      if(e === 'Activo') return 'est-activo'
      if(e === 'Retirado temporal') return 'est-retirado'
      return 'est-archivo'
    },
    mesesDesc(m){
      return [...(m.meses || [])].sort((a, b) => (b.anio * 100 + b.mes) - (a.anio * 100 + a.mes))
    },
    toggle(id){
      this.$set(this.abierto, id, !this.abierto[id])
    },
    cargar(){
      this.cargando = true
      const payload = { accion: 'estadisticas_asistencia', anio: this.anio, mes: this.mes, hora: this.hora }
      const gsel = localStorage.getItem('grupoSeleccionado')
      if(this.esSuper && gsel) payload.grupoSeleccionado = Number(gsel)
      HttpService.obtenerConConsultas('familias.php', payload).then(r => {
        this.cargando = false
        if(!r || r.error){ this.miembros = []; return }
        this.miembros = r.miembros || []
        this.totales = r.totales || { presencias: 0, posibles: 0, porcentaje: 0 }
        if(r.anios) this.anios = [...new Set([...this.anios, ...r.anios])].sort()
        if(r.horas) this.horas = [...new Set([...this.horas, ...r.horas])].sort()
      }).catch(() => { this.cargando = false; this.miembros = [] })
    }
  }
}
</script>

<style scoped>
.container{ padding: 0; }
.est-loading{ text-align: center; color: var(--text-muted); padding: 3rem 0; }
.empty-card{
  background: #fff; border: 1px dashed #d8dcee; border-radius: var(--radius-card);
  padding: 3rem 1.5rem; text-align: center; color: var(--text-muted);
  display: flex; flex-direction: column; align-items: center; gap: .6rem;
}
.empty-card .icon{ color: #c6cbe0; }

.est-panel{
  background: #fff; border: 1px solid var(--border); border-radius: var(--radius-card);
  box-shadow: var(--shadow-card); overflow: hidden;
}
.est-table{ width: 100%; font-size: .86rem; border-collapse: collapse; }
.est-table th{
  text-align: left; font-size: .68rem; font-weight: 700; letter-spacing: .05em;
  text-transform: uppercase; color: var(--text-muted);
  padding: .6rem .8rem; border-bottom: 1px solid var(--border); background: #fafbfe;
}
.est-table th.c, .est-table td.c{ text-align: center; }
.est-table td{ padding: .55rem .8rem; border-bottom: 1px solid #eef0f7; color: #3d4359; }
.est-row{ cursor: pointer; }
.est-row:hover td{ background: #f8f7ff; }
.est-nombre{ font-weight: 600; }
.est-detail td{ background: #fafbfe; padding: .9rem 1.2rem; }

.mini-wrap{ display: flex; align-items: center; gap: .45rem; justify-content: center; }
.mini-bar{ width: 70px; height: 6px; background: #e8eaf3; border-radius: 99px; overflow: hidden; }
.mini-fill{ height: 100%; border-radius: 99px; }
.mini-fill.pct-alta{ background: #28a745; }
.mini-fill.pct-media{ background: #e6a23c; }
.mini-fill.pct-baja{ background: #dc3545; }

.estado{ font-size: .72rem; font-weight: 700; padding: .15rem .5rem; border-radius: 99px; }
.est-activo{ color: #1e7e34; background: #e5f6ea; }
.est-retirado{ color: #b8770b; background: #fdf2dc; }
.est-archivo{ color: #b02a37; background: #fbe6e8; }

.pct{ font-weight: 700; font-size: .76rem; }
.pct.pct-alta{ color: #28a745; }
.pct.pct-media{ color: #e6a23c; }
.pct.pct-baja{ color: #dc3545; }

.hist-cols{ display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
@media (max-width: 640px){ .hist-cols{ grid-template-columns: 1fr; } }
.hist-sub{ font-size: .7rem; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #9aa0b8; margin-bottom: .3rem; }
.hist-table{ width: 100%; font-size: .78rem; border-collapse: collapse; }
.hist-table th{ text-align: left; color: var(--text-muted); font-weight: 600; font-size: .68rem; text-transform: uppercase; letter-spacing: .04em; padding: .25rem .3rem; border-bottom: 1px solid var(--border); }
.hist-table td{ padding: .3rem; border-bottom: 1px solid #eef0f7; color: #3d4359; }
.hist-table tbody tr:last-child td{ border-bottom: none; }
.hist-vacio{ color: var(--text-muted); font-size: .8rem; padding: .4rem 0; }
</style>
